import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output } from "@angular/core";
import { ICameraEventPeriod } from "@app/main/components/videos/models/ICameraEventPeriod";
import { DateService } from "@app/services/date.service";
import { IVideoSectionLoaded } from "../interfaces/IVideoSectionLoaded";
import './video-progress.component.scss';

@Component({
    selector: 'v-video-progress',
    template: require('./video-progress.component.html')
})
export class VVideoProgressComponent implements OnInit, OnChanges, OnDestroy {
    @Input() secondsPlayed: number;
    @Input() loadedSections: IVideoSectionLoaded[];
    @Input() activityPeriods: ICameraEventPeriod[];
    @Input() maxVideoDuration: number;
    @Input() showLargeControls: boolean;
    @Input() storyboardUrl: string;

    @Output() seek = new EventEmitter<number>();

    //--


    containerWidth: number;
    updateInterval: any;

    progressPercentage = 0.0;
    toolTipOffsetPerc = 0.0;
    toolTipText = "";
    periodTitle = "";

    thumbnailPosition: number;
    thumbnailX: number;
    thumbnailY: number;
    thumbnailClass: string;

    constructor(
        private readonly el: ElementRef,
        private readonly dateService: DateService) { }


    ngOnInit(): void {
        // We need to do this like this because the size may change over time, especially during rendering phase
        this.updateInterval = setInterval(() => {
            this.updateContainerWidth();
        }, 1000);
    }

    ngOnChanges(): void {
        this.updateProgressBarWidth();
    }

    ngOnDestroy(): void {
        clearInterval(this.updateInterval);
    }

    @HostListener('mousemove', ['$event'])
    mouseMove($event): void {
        this.mouseOver($event);
    }

    @HostListener('mouseover', ['$event'])
    mouseOver($event): void {
        this.toolTipOffsetPerc = this.getTooltipPosition($event.offsetX);
        this.updateThumbnailPosition($event.offsetX);

        this.toolTipText = this.dateService.formatTimeDuration(this.getSecondsFromXOffset($event.offsetX));
        this.periodTitle = "";

        const matchingActivityPeriod = this.activityPeriods?.find(p => this.toolTipOffsetPerc >= p.startPercentage && this.toolTipOffsetPerc <= p.endPercentage);
        if (matchingActivityPeriod) {
            this.periodTitle = matchingActivityPeriod.description;
        }
    }

    @HostListener('click', ['$event'])
    progressBarClick($event): void {
        const seekPosition = this.getSecondsFromXOffset($event.offsetX);
        this.seek.emit(seekPosition);
    }

    //--
    private updateProgressBarWidth(): void {
        this.progressPercentage = this.roundUp(this.getPositionFromTime(this.secondsPlayed), 1);

        if (this.loadedSections) {
            this.loadedSections.forEach(section => {
                section.startPercentage = this.getPositionFromTime(section.startSecond);
                section.endPercentage = this.getPositionFromTime(section.endSecond);
            });
        }

        if (this.activityPeriods) {
            this.activityPeriods.forEach(section => {
                section.startPercentage = this.getPositionFromTime(section.startSecond);
                section.endPercentage = this.getPositionFromTime(section.endSecond);
            });

        }
    }

    private updateContainerWidth(): void {
        this.containerWidth = this.getContainerWidth();
    }

    private getContainerWidth(): number {
        return this.el.nativeElement.clientWidth;
    }

    private roundUp(num, precision): number {
        const precisionPow = Math.pow(10, precision);
        return Math.floor(num * precisionPow) / precisionPow;
    }

    private getPositionFromTime(seconds: number): number {
        if (!this.maxVideoDuration || !seconds) {
            return 0;
        }

        return (seconds / this.maxVideoDuration) * 100.0;
    }

    private getTooltipPosition(offsetX: number): number {

        // Now ensure this doesnt get above or below predefined values (so that the tooltip sticks to the edge)
        const tooltipRadius = this.storyboardUrl ? 90 : 40;
        offsetX = Math.min(offsetX, this.containerWidth - tooltipRadius);
        offsetX = Math.max(offsetX, tooltipRadius);

        const position = this.getPositionPercentage(offsetX, this.containerWidth);

        return position;
    }

    private getPositionPercentage(offsetX: number, totalX: number): number {
        let position = offsetX / totalX;

        position = Math.min(position, 1);
        position = Math.max(position, 0);

        return this.roundUp(position * 100, 2);
    }

    private updateThumbnailPosition(offsetX: number): void {
        let position = this.getPositionPercentage(offsetX, this.containerWidth);

        // Scale down from 100% down to 25
        position = position / 4;

        this.thumbnailPosition = Math.round(position);
        this.thumbnailX = this.thumbnailPosition % 5;
        this.thumbnailY = Math.floor(this.thumbnailPosition / 5);

        // Special case
        if (position == 25) {
            this.thumbnailX = 4;
            this.thumbnailY = 4;
        }

        this.thumbnailClass = `thumbnail-pos-${this.thumbnailX}-${this.thumbnailY}`;

    }

    private getSecondsFromXOffset(offsetX: number): number {
        const xPercentage = offsetX / this.containerWidth;

        return xPercentage * this.maxVideoDuration;
    }
}
